import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import _ from 'lodash';

import { MUISelect } from '../MuiSelect/MuiSelect';
import { Input } from '../Input/Input';
import { Button } from '../Button/Button';
import { EmptyField } from '../EmptyField/EmptyField';
import { LogoLoader } from '../LogoLoader/LogoLoader';
import { LoaderWrapper } from '../Loader/Loader';
import { PageHeader } from '../PageHeader/PageHeader';
import { CustomAutocomplete } from '../CustomAutocomplete/CustomAutocomplete';

import classes from './customTable.module.scss';

const SearchInput = ({ value, onChange, onClear, onClick, onKeyDown, label, isLoading }) => {
  return (
    <div className={classes.CustomTableSearch}>
      <div className={classes.CustomTableSearchWrapper}>
        <Input label={label} value={value} onChange={onChange} onKeyDown={onKeyDown} />
        {value && (
          <i onClick={onClear} className={`fas fa-plus ${classes.CustomTableSearchClear}`} />
        )}
      </div>
      <Button type="primary" icon="fas fa-search" onClick={onClick} isLoading={isLoading} />
    </div>
  );
};

const typeToComponent = {
  'search-input': SearchInput,
  select: MUISelect,
  button: Button,
  autocomplete: CustomAutocomplete,
};

export const CustomTable = (props) => {
  const { build } = props;

  const {
    title,
    filters = [],
    columns = [],
    data = [],
    meta = {},
    api = {},
    table_options = {},
    is_loading = false,
    sort_by = '',
  } = build;

  const { get } = api;
  const { onClickRow, selected_cell = '', empty_message } = table_options;

  const dispatch = useDispatch();

  const params = useParams();
  const orgId = params?.teamId || '';

  const [searchData, setSearchData] = useState({
    q: '',
    page: meta?.page ? meta?.page : 1,
    per_page: meta?.per_page ? meta?.per_page : 10,
    sort: sort_by,
  });

  const [selectedCell, setSelectedCell] = useState(selected_cell || '');
  const [cellDirection, setCellDirection] = useState('asc');

  const epmtyOptions = useRef({});

  const get_options = api.get_options ?? epmtyOptions.current;

  const fetchParams = useMemo(() => {
    return { ...get_options, ...searchData, 'f[org_id]': orgId };
  }, [get_options, searchData, orgId]);

  useEffect(() => {
    if (get) {
      dispatch(get(fetchParams));
    }
  }, [dispatch, get, fetchParams]);

  const onChangeCellSort = (property) => () => {
    setSelectedCell(property);

    if (property) {
      const isAsc = searchData?.sort === property && cellDirection === 'asc';
      const direction = isAsc ? 'desc' : 'asc';

      setCellDirection(direction);

      if (direction === 'asc') {
        setSearchData((prev) => ({ ...prev, sort: `${property}` }));
      } else {
        setSearchData((prev) => ({ ...prev, sort: `-${property}` }));
      }
    }
  };

  const onChangeFilter = useCallback(
    (key) => (event) => {
      setSearchData((prev) => ({ ...prev, [key]: event?.target?.value }));
    },
    [],
  );

  const onClearFilter = useCallback(
    (key) => () => {
      setSearchData((prev) => ({ ...prev, [key]: '' }));
    },
    [],
  );

  const searchOnKeyDown = useCallback(
    (event) => {
      if (event.keyCode === 13 && searchData?.q) {
        return dispatch(get({ ...get_options, ...searchData, 'f[org_id]': orgId }));
      }
    },
    [dispatch, get, get_options, searchData, orgId],
  );

  const onClickSearchButton = useCallback(() => {
    dispatch(get({ ...get_options, ...searchData, 'f[org_id]': orgId }));
  }, [dispatch, get, get_options, orgId, searchData]);

  const onRowClick = useCallback(
    (item) => () => {
      if (onClickRow) {
        return onClickRow(item);
      }
    },
    [onClickRow],
  );

  const onPageChange = useCallback((event, page) => {
    setSearchData((prev) => ({ ...prev, page: page + 1 }));
  }, []);

  const onChangePageSize = useCallback((event) => {
    setSearchData((prev) => ({ ...prev, page: 1, per_page: parseFloat(event.target.value) }));
  }, []);

  return (
    <div className={classes.CustomTable}>
      {title && (
        <PageHeader title={title}>
          <div className={classes.CustomTableFilters}>
            {filters
              ?.filter((item) => !_.isEmpty(item))
              ?.map((item) => {
                const Component = !item?.component
                  ? typeToComponent?.[item?.type]
                  : item?.component;

                const className =
                  item.type !== 'autocomplete'
                    ? classes.CustomTableFiltersItem
                    : classes.CustomTableFiltersItemSelect;

                return (
                  <div className={className} key={item.id}>
                    <Component
                      value={searchData?.[item?.id]}
                      label={item?.label}
                      options={item?.data}
                      onChange={onChangeFilter(item?.id)}
                      onClear={onClearFilter(item?.id)}
                      onKeyDown={searchOnKeyDown}
                      onClick={onClickSearchButton}
                      {...item?.options}
                    />
                  </div>
                );
              })}
          </div>
        </PageHeader>
      )}
      <Paper>
        {is_loading ? (
          <EmptyField>
            <LoaderWrapper>
              <LogoLoader />
            </LoaderWrapper>
          </EmptyField>
        ) : (
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <TableContainer sx={{ minWidth: columns.length * 160 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {columns
                      ?.filter((item) => !_.isEmpty(item))
                      .map((item) => (
                        <TableCell key={item.id}>
                          {item.sortable ? (
                            <TableSortLabel
                              active={selectedCell === item.id}
                              direction={cellDirection}
                              onClick={onChangeCellSort(item.id)}
                            >
                              {item.name}
                            </TableSortLabel>
                          ) : (
                            item.name
                          )}
                        </TableCell>
                      ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {data?.map((row) => (
                    <TableRow
                      key={row.id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      onClick={onRowClick(row)}
                    >
                      {columns
                        ?.filter((c) => !_.isEmpty(c))
                        ?.map((column) => {
                          const value =
                            typeof column?.value === 'function'
                              ? column?.value(row)
                              : row?.[column?.id];

                          const style =
                            typeof column?.style === 'function'
                              ? column?.style(row)
                              : column?.style;

                          return (
                            <TableCell
                              key={column.id}
                              component="th"
                              scope="row"
                              sx={{ whiteSpace: 'nowrap', ...style }}
                            >
                              {value}
                            </TableCell>
                          );
                        })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
        {!data.length && empty_message ? <EmptyField title={empty_message} /> : null}
        <TablePagination
          rowsPerPageOptions={[10, 20, 30, 40, 50]}
          component="div"
          count={meta?.total ?? 0}
          rowsPerPage={searchData?.per_page}
          page={searchData?.page - 1}
          onPageChange={onPageChange}
          onRowsPerPageChange={onChangePageSize}
        />
      </Paper>
    </div>
  );
};
